import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { Icon } from '@iconify/react';
import getCroppedImg, { createImage } from '../../utils/canvasUtils';

// Predefined Aspect Ratios
const ASPECT_RATIOS = [
    { label: 'Thumbnail (4:3)', value: 4 / 3 },
    { label: 'Feed/Blog (3:2)', value: 3 / 2 },
    { label: 'Venue Header (3:2)', value: 3 / 2 },
    { label: 'Profile (1:1)', value: 1 / 1 },
];

// Downsample threshold to improve performance on large images (4K+)
const DOWNSAMPLE_THRESHOLD = 2000;

const ImageCropperModal = ({
    show,
    onHide,
    imageSrc,
    onCropComplete,
    fixedAspectRatio, // If provided, locks to this ratio
    initialAspectRatio = 4 / 3, // Default if not locked
    minWidth = 750,
    outputFormat = 'image/jpeg',
    quality = 0.8,
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [aspect, setAspect] = useState(fixedAspectRatio || initialAspectRatio);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    
    // State for downsampled image (optimization for large images)
    const [displayImageSrc, setDisplayImageSrc] = useState(null);
    const [scale, setScale] = useState(1);
    const [loadingImage, setLoadingImage] = useState(false);

    // Update aspect if fixedAspectRatio changes
    useEffect(() => {
        if (fixedAspectRatio) {
            setAspect(fixedAspectRatio);
        }
    }, [fixedAspectRatio]);

    // Optimize large images by downsampling for display
    useEffect(() => {
        let active = true;
        let objectUrl = null;

        const processImage = async () => {
            if (!imageSrc || !show) return;

            setLoadingImage(true);
            try {
                const image = await createImage(imageSrc);
                
                // If image is smaller than threshold, use original
                if (image.width <= DOWNSAMPLE_THRESHOLD) {
                    if (active) {
                        setDisplayImageSrc(imageSrc);
                        setScale(1);
                        setLoadingImage(false);
                    }
                    return;
                }

                // Downsample logic
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Calculate new dimensions (max width DOWNSAMPLE_THRESHOLD)
                const ratio = DOWNSAMPLE_THRESHOLD / image.width;
                const newWidth = DOWNSAMPLE_THRESHOLD;
                const newHeight = image.height * ratio;

                canvas.width = newWidth;
                canvas.height = newHeight;
                
                ctx.drawImage(image, 0, 0, newWidth, newHeight);

                // Get blob from canvas
                canvas.toBlob((blob) => {
                    if (!active) return;
                    
                    if (blob) {
                        objectUrl = URL.createObjectURL(blob);
                        setDisplayImageSrc(objectUrl);
                        setScale(1 / ratio); // Scale factor to map back to original
                    } else {
                        // Fallback to original if blob creation fails
                        setDisplayImageSrc(imageSrc);
                        setScale(1);
                    }
                    setLoadingImage(false);
                }, 'image/jpeg', 0.9);

            } catch (error) {
                console.error('Error processing image for cropper:', error);
                if (active) {
                    setDisplayImageSrc(imageSrc);
                    setScale(1);
                    setLoadingImage(false);
                }
            }
        };

        processImage();

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [imageSrc, show]);

    const onCropChange = useCallback((crop) => {
        setCrop(crop);
    }, []);

    const onZoomChange = useCallback((zoom) => {
        setZoom(zoom);
    }, []);

    const onRotationChange = useCallback((rotation) => {
        setRotation(rotation);
    }, []);

    const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels || !imageSrc) return;
        
        setIsCropping(true);
        try {
            // Map cropped pixels back to original image size
            const scaledPixels = {
                x: croppedAreaPixels.x * scale,
                y: croppedAreaPixels.y * scale,
                width: croppedAreaPixels.width * scale,
                height: croppedAreaPixels.height * scale,
            };

            const croppedImageBlob = await getCroppedImg(
                imageSrc, // Use original high-res source
                scaledPixels,
                rotation,
                { horizontal: false, vertical: false },
                minWidth,
                outputFormat,
                quality
            );
            
            // Create a File object from the Blob
            const fileName = `cropped_${Date.now()}.${outputFormat === 'image/jpeg' ? 'jpg' : 'webp'}`;
            const file = new File([croppedImageBlob], fileName, { type: outputFormat });
            
            onCropComplete(file, URL.createObjectURL(croppedImageBlob));
            onHide();
        } catch (e) {
            console.error('Cropping failed', e);
            alert(e.message || 'An error occurred while cropping the image.');
        } finally {
            setIsCropping(false);
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            centered 
            size="lg" 
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title className="text-lg fw-semibold d-flex align-items-center gap-2">
                    <Icon icon="solar:crop-minimalistic-linear" className="text-xl" />
                    Crop Image
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="position-relative bg-dark" style={{ height: '400px', width: '100%' }}>
                    {loadingImage ? (
                        <div className="d-flex align-items-center justify-content-center h-100 text-white">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading image...
                        </div>
                    ) : (
                        <Cropper
                            image={displayImageSrc}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={aspect}
                            onCropChange={onCropChange}
                            onCropComplete={onCropCompleteHandler}
                            onZoomChange={onZoomChange}
                            onRotationChange={onRotationChange}
                            showGrid={true}
                        />
                    )}
                </div>
                
                <div className="p-4 bg-white border-top">
                    <div className="row g-3 align-items-center mb-3">
                        <div className="col-md-6">
                            <label className="form-label text-sm fw-medium text-secondary-light">Zoom</label>
                            <div className="d-flex align-items-center gap-2">
                                <Icon icon="solar:magnifier-zoom-out-linear" className="text-secondary-light" />
                                <Form.Range 
                                    min={1} 
                                    max={3} 
                                    step={0.1} 
                                    value={zoom} 
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                    className="flex-grow-1"
                                />
                                <Icon icon="solar:magnifier-zoom-in-linear" className="text-secondary-light" />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label text-sm fw-medium text-secondary-light">Rotation</label>
                            <div className="d-flex align-items-center gap-2">
                                <Icon icon="solar:refresh-linear" className="text-secondary-light" />
                                <Form.Range 
                                    min={0} 
                                    max={360} 
                                    step={1} 
                                    value={rotation} 
                                    onChange={(e) => setRotation(Number(e.target.value))}
                                    className="flex-grow-1"
                                />
                                <span className="text-xs text-secondary fw-medium w-32-px text-end">{rotation}°</span>
                            </div>
                        </div>
                    </div>

                    {!fixedAspectRatio && (
                        <div className="mb-3">
                            <label className="form-label text-sm fw-medium text-secondary-light d-block">Aspect Ratio</label>
                            <div className="d-flex flex-wrap gap-2">
                                {ASPECT_RATIOS.map((ratio) => (
                                    <Button
                                        key={ratio.label}
                                        variant={Math.abs(aspect - ratio.value) < 0.01 ? "primary-600" : "outline-secondary"}
                                        size="sm"
                                        onClick={() => setAspect(ratio.value)}
                                        className="rounded-pill px-3"
                                    >
                                        {ratio.label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={isCropping}>
                    Cancel
                </Button>
                <Button variant="primary-600" onClick={handleSave} disabled={isCropping} className="d-flex align-items-center gap-2">
                    {isCropping && <span className="spinner-border spinner-border-sm" />}
                    Confirm Crop
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default React.memo(ImageCropperModal);
